<template>
  <div class="as-a-coach-accepted-inspection-requests-table">
    <!-- Top Search Bar -->
    <div class="search-bar-wrapper">
      <SearchBar
        v-if="items.length > 0"
        :label="appliedFilters.length > 0 ? 'More Filters' : 'Filter'"
        :length="appliedFilters.length"
        ghostText="Search"
        :filterBtn="true"
        :chipText="appliedFilterLabels"
        :valueSearch="searchQuery"
        @onClick="handleSearchBarClick"
        @onSearch="handleSearch"
        @removeFilter="handleRemoveFilter"
        class="flex-grow-1"
        removeColumnClass
      />
    </div>

    <!-- Table -->
    <div class="table-container no-border">
      <table v-if="paginatedItems.length > 0" class="inspection-table">
        <thead>
          <tr>
            <th class="header-cell pl-6" width="18%">Employee</th>
            <th class="header-cell" width="40%">Inspection Level requested for Responsibility Assignment</th>
            <th class="header-cell" width="12%">Inspection Status</th>
            <th class="header-cell pr-6" width="30%"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedItems" :key="item.id" class="table-row">
            <!-- Employee Column -->
            <td class="cell pl-6 py-4">
              <EmployeeCard
                :firstName="item.employee.firstName"
                :lastName="item.employee.lastName"
                :position="item.employee.position"
                :photoUrl="item.employee.photoUrl"
                :showAvailability="false"
                :isHoverEnabled="false"
              />
            </td>

            <!-- Responsibility Assignment Column -->
            <td class="cell py-4">
              <div class="assignment-cell-wrapper">
                <div class="readiness-transition-container">
                  <MinimalisticReadinessLevel :level="item.currentBadgeLevel" />
                  <span class="readiness-separator">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L15 12L9 6" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                  <MinimalisticReadinessLevel :level="item.requestedLevel || 'apprentice'" />
                </div>
                <div class="dropdown-container" :class="{ 'full-width': !item.isExpedited }">
                  <ResponsibilityAssignmentDropDown
                    :responsibilityCode="item.assignment.code"
                    :responsibilityName="item.assignment.name"
                    :show_codes="true"
                    class="full-width-dropdown"
                  />
                </div>
                <span
                  v-if="item.isExpedited"
                  class="expedited-badge-chip"
                >
                  Expedited
                </span>
              </div>
            </td>

            <!-- Checklist progress (pass/fail) widget -->
            <td class="cell py-4">
              <InspectionIssueBadge
                :currentBadgeLevel="item.currentBadgeLevel"
                :readyFor="item.readyFor || item.requestedLevel"
                :checkpoints="item.checkpoints || []"
                :currentBadgeStatus="item.currentBadgeStatus || 'good_standing'"
              />
            </td>

            <!-- Actions Column -->
            <td class="cell pr-6 py-4">
              <div class="actions-container">
                <!-- Action Buttons -->
                <div class="action-btn-box">
                  <IssueBadgeButton
                    v-if="item.actionButton"
                    :label="item.actionButton.label"
                    :tooltipText="item.actionButton.tooltip"
                    :backgroundColor="item.actionButton.color"
                    buttonType="primary"
                    @onClick="handleAward(item)"
                  />
                </div>

                <!-- Fail / Decline buttons (both require a reason) -->
                <div class="fail-decline-box">
                  <StandardButton
                    type="red"
                    variant="primary"
                    size="small"
                    label="Fail"
                    @click="openReasonModal(item, 'fail')"
                  />
                  <StandardButton
                    type="red"
                    variant="outline"
                    size="small"
                    label="Decline"
                    @click="openReasonModal(item, 'decline')"
                  />
                </div>

                <!-- Chatter Icon -->
                <div class="chatter-box">
                  <ChatterIcon
                    :unread="item.unreadMessages"
                    @onClick="handleChatterClick(item)"
                  />
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty States -->
      <NoRecordFound
        v-else-if="searchQuery || appliedFilters.length > 0"
        title="Inspection"
        message="requests"
        btnTitle="Request"
        :showSpecificMessage="'No requests found'"
        :description="'Your filter or search did not match any requests. Please try again.'"
        :showClearAction="true"
        clearLabel="Clear search"
        :noBorder="true"
        :shouldShowAddAction="false"
        :showActiveCounter="false"
        @onClear="clearAllFilters"
      />
      <NoRecordFound
        v-else
        title="Inspection"
        message="requests"
        btnTitle="Request"
        :showSpecificMessage="'No requests'"
        :description="'There are currently no requests to process.'"
        :noBorder="true"
        :shouldShowAddAction="false"
        :showActiveCounter="false"
      />
    </div>

    <!-- Pagination -->
    <div v-if="showPaginationSection" class="pagination-section">
      <ExPagination
        :pagination="pagination"
        @onRequest="handlePaginationRequest"
      />
    </div>

    <!-- Filter Modal (Placeholder for SearchModal) -->
    <SearchModal
      :dialog="showFilterModal"
      filterType="Inspection"
      :statusOptions="statusOptions"
      :filterGroups="filterGroups"
      :defaultSelected="appliedFilterLabels"
      @back="showFilterModal = false"
      @checkedItemsChanged="handleFiltersChanged"
    />

    <!-- Required-reason modal for Fail / Decline -->
    <ReasonModal
      :showDialog="reasonModal.show"
      :title="reasonModal.config.title"
      :description="reasonModal.config.description"
      :placeHolder="reasonModal.config.placeHolder"
      :btnLabel="reasonModal.config.btnLabel"
      :btnDanger="reasonModal.config.btnDanger"
      :btnColor="reasonModal.config.btnColor"
      v-model="reason"
      @onClose="closeReasonModal"
      @onAction="handleReasonAction"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import EmployeeCard from '@components/organizations/employees/EmployeeCard/EmployeeCard.vue';
import ResponsibilityAssignmentDropDown from '@components/ResponsibilityAssignmentDropDown/ResponsibilityAssignmentDropDown.vue';
import MinimalisticReadinessLevel from '@components/MinimalisticReadinessLevel/MinimalisticReadinessLevel.vue';
import InspectionIssueBadge from '@components/InspectionIssueBadge/InspectionIssueBadge.vue';
import IssueBadgeButton from '@components/IssueBadgeButton/IssueBadgeButton.vue';
import StandardButton from '@components/StandardButton/StandardButton.vue';
import ReasonModal from '@components/ReasonModal/ReasonModal.vue';
import ChatterIcon from '@components/ChatterIcon/ChatterIcon.vue';
import SearchBar from '@components/SearchBar/SearchBar.vue';
import SearchModal from '@components/SearchBar/SearchModal.vue';
import NoRecordFound from '@components/NoRecordFound/NoRecordFound.vue';
import ExPagination from '@components/DataTable/ExPagination.vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['award', 'fail', 'decline', 'chatter-click']);

const searchQuery = ref('');
const appliedFilters = ref([]);
const showFilterModal = ref(false);
const currentPage = ref(1);
const ITEMS_PER_PAGE = 10;

const statusOptions = ['Passed', 'Failed', 'Pending'];

// Pagination object for ExPagination
const pagination = computed(() => ({
  page: currentPage.value,
  total: filteredItems.value.length,
  perPage: ITEMS_PER_PAGE
}));

const handlePaginationRequest = (data) => {
  currentPage.value = data.page;
};

// Filter groups for SearchModal
const filterGroups = [
  {
    label: 'Type',
    items: [
      { label: 'Expedited', value: 'Expedited' },
      { label: 'Standard', value: 'Standard' },
    ],
  },
];

const filteredItems = computed(() => {
  let result = props.items;

  // Search by text (Employee name or Responsibility name/code)
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(item => {
      const employeeName = `${item.employee.firstName} ${item.employee.lastName}`.toLowerCase();
      const responsibilityName = item.assignment.name.toLowerCase();
      const responsibilityCode = item.assignment.code.toLowerCase();
      return employeeName.includes(query) || 
             responsibilityName.includes(query) || 
             responsibilityCode.includes(query);
    });
  }

  // Filter by Modal selections
  if (appliedFilters.value.length > 0) {
    result = result.filter(item => {
      return appliedFilters.value.every(filter => {
        // Status filters from statusOptions (checkboxes) don't have labelType
        if (!filter.labelType || filter.labelType === 'Status') {
          return item.checkpoints.some(c => {
            const status = c.evaluation_status === 'passed' ? 'Passed' : 
                          c.evaluation_status === 'failed' ? 'Failed' : 'Pending';
            return status === filter.value;
          });
        }
        if (filter.labelType === 'Type') {
          if (filter.value === 'Expedited') return item.isExpedited;
          if (filter.value === 'Standard') return !item.isExpedited;
        }
        return true;
      });
    });
  }

  return result;
});

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / ITEMS_PER_PAGE);
});

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * ITEMS_PER_PAGE;
  const end = start + ITEMS_PER_PAGE;
  return filteredItems.value.slice(start, end);
});

const showPaginationSection = computed(
  () => filteredItems.value.length > ITEMS_PER_PAGE
);

const handleSearchBarClick = (type) => {
  if (type === 'filter') {
    showFilterModal.value = true;
  }
};

const handleSearch = (value) => {
  searchQuery.value = value;
  currentPage.value = 1; // Reset to first page on search
};

const handleFiltersChanged = (filters) => {
  appliedFilters.value = filters;
  showFilterModal.value = false;
  currentPage.value = 1; // Reset to first page on filter change
};

const appliedFilterLabels = computed(() => {
  return appliedFilters.value.map(f => f.value);
});

const handleRemoveFilter = (removedValue) => {
  appliedFilters.value = appliedFilters.value.filter(f => f.value !== removedValue);
  currentPage.value = 1; // Reset to first page on filter removal
};

const clearAllFilters = () => {
  searchQuery.value = '';
  appliedFilters.value = [];
  currentPage.value = 1;
};

// Required-reason modal for Fail / Decline. Both are destructive actions and
// neither may be a single click: the confirm button stays disabled until a
// non-empty reason of at least 5 characters is supplied (ReasonModal minLength).
const reason = ref('');
const reasonModal = ref({
  show: false,
  type: null,
  config: {},
});
const activeItem = ref(null);

const modalConfigs = {
  fail: {
    title: 'Fail Inspection Request',
    description: 'Please provide a reason for failing this inspection request.',
    placeHolder: 'Enter a reason',
    btnLabel: 'Fail',
    btnDanger: true,
    btnColor: '#d92d20',
  },
  decline: {
    title: 'Decline Inspection Request',
    description: 'Please provide a reason for declining this inspection request.',
    placeHolder: 'Enter a reason',
    btnLabel: 'Decline',
    btnDanger: true,
    btnColor: '#d92d20',
  },
};

const openReasonModal = (item, type) => {
  activeItem.value = item;
  reason.value = '';
  reasonModal.value = {
    show: true,
    type,
    config: modalConfigs[type],
  };
};

const closeReasonModal = () => {
  reasonModal.value = { show: false, type: null, config: {} };
  reason.value = '';
  activeItem.value = null;
};

const handleReasonAction = () => {
  const trimmedReason = reason.value.trim();
  if (trimmedReason.length < 5 || trimmedReason.length > 500) {
    return;
  }
  const item = activeItem.value;
  const type = reasonModal.value.type;
  if (!item || !type) {
    return;
  }
  if (type === 'fail') {
    emit('fail', item, trimmedReason);
  } else if (type === 'decline') {
    emit('decline', item, trimmedReason);
  }
  closeReasonModal();
};

const handleAward = (item) => {
  emit('award', item);
};

const handleChatterClick = (item) => {
  emit('chatter-click', item);
};

defineExpose({
  handleSearch,
  handleFiltersChanged,
  clearAllFilters,
  handlePaginationRequest,
  openReasonModal,
  closeReasonModal,
  handleReasonAction,
  handleAward,
  handleChatterClick,
  reason,
  reasonModal,
  filteredItems,
  currentPage,
  totalPages,
  searchQuery,
  appliedFilters,
});
</script>

<style scoped>
.as-a-coach-accepted-inspection-requests-table {
  padding: 24px;
  background: #ffffff;
  font-family: Inter, sans-serif;
}

.search-bar-wrapper {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  padding: 0 20px;
}

.flex-grow-1 {
  flex-grow: 1;
}

.table-container {
  overflow-x: auto;
  border-radius: 8px;
}

.table-container.no-border {
  border: none !important;
  box-shadow: none !important;
}

.inspection-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1100px;
}

.header-cell {
  background-color: #F9FAFB;
  height: 44px;
  padding: 12px 24px;
  font-size: 12px;
  color: #667085;
  text-align: left;
  font-weight: 500;
  border-bottom: 1px solid #EAECF0;
  white-space: nowrap;
}

.cell {
  padding: 16px;
  border-bottom: 1px solid #EAECF0;
  vertical-align: middle;
}

.pl-6 { padding-left: 24px !important; }
.pr-6 { padding-right: 24px !important; }
.py-4 { padding-top: 16px !important; padding-bottom: 16px !important; }

.table-row:last-child .cell {
  border-bottom: none;
}

.assignment-cell-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.readiness-transition-container {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.readiness-separator {
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.dropdown-container {
  flex: 1;
  max-width: 540px;
  min-width: 280px;
}

.dropdown-container.full-width {
  max-width: none;
}

.expedited-badge-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #FDF2FA;
  color: #C11574;
  border: 1px solid #FCCEEE;
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;
}

.actions-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.action-btn-box {
  min-width: 120px;
  display: flex;
  justify-content: flex-end;
}

.fail-decline-box {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.chatter-box {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.pagination-section {
  padding-top: 20px;
  border-top: 1px solid #EAECF0;
  margin-top: 24px;
}

/* Override for external components if needed */
:deep(.full-width-dropdown) {
  width: 100% !important;
}

:deep(.dropdown-container .rad__trigger-btn) {
  width: 100% !important;
}

:deep(.no-record-found) {
  padding: 40px 0;
}

:deep(.issue-badge-button) {
  text-transform: none !important;
}

:deep(.search-bar-wrapper .search-button) {
  max-width: 35%;
}
</style>
